import { stringCompare, useIsFeatureEnabled } from "@quortex/front-common"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { pick } from "lodash-es"
import { useMemo } from "react"
import { useInputList } from "src/queries/playInputs"
import { useOttEndpointList } from "src/queries/playOttEndpoints"
import { useTarget, useTargetList } from "src/queries/playTargets"
import type {
  UpdatedInputEndpointRow,
  UpdatedL2VCollectionEndpointRow
} from "@/App/play/components/features/endpoints/ReviewChanges"
import { longRefetchOptions } from "@/App/play/helpers/input"
import { useL2VCollectionWithEndpointList } from "@/queries/live2Vod/playL2VAssetCollections"
import { useProcessingList } from "@/queries/playProcessings"
import type { Schema } from "./types"

export const getDefaultValuesForCreate = (target: Partial<api.ott.Target>): Schema =>
  pick(target, [
    "container",
    "dashAdvanced",
    "encryptionDynamic",
    "encryptionStatic",
    "encryptionType",
    "hlsAdvanced",
    "latency",
    "playlistLength",
    "segmentDuration",
    "scte_35",
    "type"
  ])

export const getDefaultValuesForUpdate = (target: Partial<api.ott.Target>): Schema => ({
  latency: target.latency ?? "standard",
  ...pick(target, [
    "container",
    "dashAdvanced",
    "encryptionDynamic",
    "encryptionStatic",
    "encryptionType",
    "hlsAdvanced",
    "identifier",
    "name",
    "playlistLength",
    "segmentDuration",
    "scte_35",
    "type",
    "uuid"
  ])
})

export const extensionByPackagingType = {
  dash: "mpd",
  hls: "m3u8"
}

const getFieldValue = <T>(presetValue: T | null | undefined, defaultValue: T): T => {
  return presetValue == null ? defaultValue : presetValue
}

export const getDefaultValuesForAdvancedForm = (
  presetValues: Partial<api.ott.Target>,
  poolType: api.ott.Pool["type"],
  isJitpDefaultRoutingEnabled: boolean,
  isDashAvc3SignallingEnabled: boolean,
  isLatencyLow: boolean,
  hasConfigurations = false
) => {
  const isPremium = poolType === "premium"

  const position = getFieldValue(
    presetValues?.dashAdvanced?.position,
    hasConfigurations && !isJitpDefaultRoutingEnabled && !isPremium ? "representation" : null
  )
  const timelinePosition = isJitpDefaultRoutingEnabled
    ? getFieldValue(presetValues?.dashAdvanced?.timelinePosition, "adaptation_set")
    : null
  const minimumBufferTime = isJitpDefaultRoutingEnabled
    ? getFieldValue(presetValues?.dashAdvanced?.minimumBufferTime, 2)
    : null
  const startTimeOriginOffset = isPremium
    ? null
    : getFieldValue(presetValues?.dashAdvanced?.startTimeOriginOffset, 0)
  const segmentFormat = isPremium
    ? null
    : getFieldValue(presetValues?.dashAdvanced?.segmentFormat, "index_based")
  const videoCodecSignalling = isDashAvc3SignallingEnabled
    ? getFieldValue(presetValues?.dashAdvanced?.videoCodecSignalling, "avc1")
    : null

  const version = isPremium ? null : getFieldValue(presetValues?.hlsAdvanced?.version, 7)
  const mediaPathType = isPremium
    ? null
    : getFieldValue(presetValues?.hlsAdvanced?.mediaPathType, "relative")

  const contentPathType = isPremium
    ? null
    : getFieldValue(presetValues?.hlsAdvanced?.contentPathType, "relative")

  const minimumUpdatePeriod = getFieldValue(
    presetValues?.dashAdvanced?.minimumUpdatePeriod,
    isJitpDefaultRoutingEnabled && isLatencyLow ? 1 : null
  )

  return {
    dashAdvanced: {
      ...presetValues?.dashAdvanced,
      minimumBufferTime,
      minimumUpdatePeriod,
      position,
      segmentFormat,
      startTimeOriginOffset,
      timelinePosition,
      videoCodecSignalling
    },
    hlsAdvanced: { ...presetValues?.hlsAdvanced, contentPathType, mediaPathType, version }
  }
}

export const useUpdatedEndpointRows = (): {
  ottEndpointRowList: UpdatedInputEndpointRow[]
  l2vCollectionEndpointRowList: UpdatedL2VCollectionEndpointRow[]
} => {
  const { data: target } = useSuspenseQuery(useTarget())
  const { data: processingList } = useSuspenseQuery({
    ...useProcessingList(),
    select: data => data.results
  })

  const { data: targetList } = useSuspenseQuery({
    ...useTargetList(),
    select: data => data.results
  })

  const { data: ottEndpointList } = useSuspenseQuery({
    ...useOttEndpointList(),
    select: data => data.results
  })
  const { data: inputList } = useSuspenseQuery({
    ...useInputList(),
    ...longRefetchOptions,
    select: data => data?.results
  })
  const isLive2VodEnabled = useIsFeatureEnabled("play_live_2_vod")
  const isJitpEnabled = useIsFeatureEnabled("jitp_default_routing")

  const { data: collectionWithEndpointList } = useQuery({
    ...useL2VCollectionWithEndpointList(),
    ...longRefetchOptions,
    enabled: isLive2VodEnabled && isJitpEnabled,
    select: data => data?.results
  })

  return useMemo(() => {
    const l2vCollectionEndpointRowList: UpdatedL2VCollectionEndpointRow[] = (
      collectionWithEndpointList ?? []
    )
      .map(collection => ({
        collectionName: collection.collectionName ?? "",
        collectionUuid: collection.collectionUuid,
        l2vEndpointList: collection.collectionEndpoints
          .filter(endpoint => endpoint.packagingUuid === target.uuid)
          .map(endpoint => ({
            ...endpoint,
            target: targetList.find(target => target.uuid === endpoint.packagingUuid)
          }))
      }))
      .filter(collection => collection.l2vEndpointList.length > 0)
      .toSorted((a, b) => stringCompare(a.collectionName, b.collectionName))

    const ottEndpointRowList = (inputList ?? [])
      .map(input => ({
        inputName: input.name ?? "",
        inputUuid: input.uuid,
        ottEndpointList: ottEndpointList
          .filter(endpoint => endpoint.inputUuid === input.uuid)
          .filter(endpoint => endpoint.targetUuid === target.uuid)
          .map(endpoint => ({
            ...endpoint,
            input,
            processing: processingList.find(
              processing => processing.uuid === endpoint.processingUuid
            ),
            target: targetList.find(target => target.uuid === endpoint.targetUuid)
          }))
          .toSorted((a, b) => stringCompare(a.pretty || a.customPath, b.pretty || b.customPath))
      }))
      .filter(row => row.ottEndpointList.length > 0)
      .toSorted((a, b) => stringCompare(a.inputName, b.inputName))

    return { l2vCollectionEndpointRowList, ottEndpointRowList }
  }, [
    inputList,
    ottEndpointList,
    target.uuid,
    processingList,
    targetList,
    collectionWithEndpointList
  ])
}
